import React, { useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { useVenue } from "./VenueProvider";
import Loader from "./Loader";
import AnaliticasWeb from "./AnaliticasWeb";
import { calendarYear } from "../utils/dates";
import { relativeTime, leadDesgloseTotal } from "../utils/leadHelpers";
import { getValorEvento, eventoPagado } from "../utils/valorEvento";
import {
  buildOccupiedSet,
  mesOcupacion,
  anioOcupacion,
  yearsInSet,
  ocupacionPorDiaSemana,
} from "../utils/ocupacion";
import OcupacionMesDialog from "./OcupacionMesDialog";
import { Badge } from "./badge";
import {
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  ChartBarIcon,
  InformationCircleIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  ClockIcon,
  EnvelopeIcon,
  ChevronRightIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const MONTHS_SHORT = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const YEAR_COLORS = { prev: "#93c5a8", current: "#1b4d2e", next: "#6366F1" };

const formatMXN = (v) => {
  const n = Number(v || 0);
  return n < 0 ? `-$${Math.abs(n).toLocaleString("es-MX")}` : `$${n.toLocaleString("es-MX")}`;
};

// Time-aware greeting
const getGreeting = (name) => {
  const h = new Date().getHours();
  const n = name || "Usuario";
  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
  if (h >= 5 && h < 12) {
    return pick([`Buenos días, ${n}`, `Arriba, ${n}`, `Qué onda, ${n}`]);
  }
  if (h >= 12 && h < 19) {
    return pick([`Buenas tardes, ${n}`, `Hola, ${n}`, `Por fin, ${n}`]);
  }
  if (h >= 19 && h < 22) {
    return pick([`Buenas noches, ${n}`, `De vuelta, ${n}`, `Ahí andas, ${n}`]);
  }
  // 22-5
  return pick([`${n}, son las ${h}:${String(new Date().getMinutes()).padStart(2, "0")}`, `A estas horas, ${n}`, `Sin dormir, ${n}`]);
};

const Section = ({ icon: Icon, title, children, className = "", action }) => (
  <div className={`rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm ${className}`}>
    <div className="flex items-center gap-2 mb-4">
      <div
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: ACCENT + "1a" }}
      >
        <Icon className="h-4 w-4" style={{ color: ACCENT }} />
      </div>
      <span className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
        {title}
      </span>
      {action && <div className="ml-auto">{action}</div>}
    </div>
    {children}
  </div>
);

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label, valueFormatter }) => {
  if (!active || !payload?.length) return null;
  const fmt = valueFormatter || ((v) => v);
  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-3 shadow-lg text-sm">
      <div className="font-semibold text-zinc-900 dark:text-white mb-1">{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2">
          <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-zinc-500">{entry.name}:</span>
          <span className="font-medium" style={{ color: entry.color }}>
            {fmt(entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

// Info hint with instant tooltip
const InfoHint = ({ text }) => (
  <span className="group relative inline-flex">
    <InformationCircleIcon className="h-4 w-4 text-zinc-400 cursor-help" />
    <span className="pointer-events-none absolute right-0 top-6 z-10 w-48 rounded-lg bg-zinc-800 px-3 py-2 text-xs text-white shadow-lg opacity-0 transition-opacity group-hover:opacity-100">
      {text}
    </span>
  </span>
);

const HOME_TABS = ["resumen", "analiticas"];

const toJsDate = (d) => {
  if (!d) return null;
  if (typeof d.toDate === "function") return d.toDate();
  if (typeof d.seconds === "number") return new Date(d.seconds * 1000);
  if (typeof d === "string" || typeof d === "number") return new Date(d);
  return null;
};

// "hoy" / "mañana" / "en X días" para próximos eventos
const daysAway = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target - today) / (24 * 60 * 60 * 1000));
  if (diff <= 0) return "hoy";
  if (diff === 1) return "mañana";
  return `en ${diff} días`;
};

const Home = () => {
  const { userInfo } = useAuth();
  const { selectedVenue, venues, loadsLoading, socialClicksLoading, contactFormsLoading, venueLoading, events, payments, contactForms, calendarTimezone } = useVenue();
  const navigate = useNavigate();
  // Convención de la plataforma: los viewers ven agregados y estados, pero
  // NUNCA montos por evento (EventPage les oculta cotización/pagos/depósitos).
  const isViewer = userInfo?.role === "viewer";
  const [greetingText] = useState(() => getGreeting(userInfo?.name));
  // La pestaña vive en la URL (?tab=analiticas) — mismo patrón que los
  // filtros del kanban de leads: sobrevive recargas y navegación atrás.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const homeTab = HOME_TABS.includes(tabParam) ? tabParam : "resumen";
  const setHomeTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === "resumen") next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next, { replace: true });
  };

  const loading = venueLoading || loadsLoading || socialClicksLoading || contactFormsLoading;

  const eventStats = useMemo(() => {
    if (!events || !events.length) return null;
    const now = new Date();
    const currentYear = now.getFullYear();

    const parsed = events
      .filter(e => e.eventType === "event" && e.status !== "cancelado")
      .map(e => {
        let d = e.startTime;
        if (d?.seconds) d = new Date(d.seconds * 1000);
        else if (d?.toDate) d = d.toDate();
        else if (typeof d === "string" || typeof d === "number") d = new Date(d);
        return { ...e, _date: d };
      })
      .filter(e => e._date instanceof Date && !isNaN(e._date));

    const upcoming = parsed.filter(e => e._date > now).length;

    const byYear = {};
    parsed.forEach(e => {
      const y = e._date.getFullYear();
      byYear[y] = (byYear[y] || 0) + 1;
    });

    // Solo años que SÍ tienen eventos (incluyendo futuros ya reservados),
    // los 3 más recientes — igual que la ocupación por día de la semana.
    const yearsAll = Object.keys(byYear).map(Number).sort((a, b) => a - b);
    const years = yearsAll.length > 0 ? yearsAll.slice(-3) : [currentYear];
    const monthly = Array.from({ length: 12 }, (_, i) => {
      const row = { month: MONTHS_SHORT[i] };
      years.forEach(y => { row[y] = 0; });
      return row;
    });
    parsed.forEach(e => {
      const y = e._date.getFullYear();
      if (years.includes(y)) monthly[e._date.getMonth()][y]++;
    });

    const totalCobrado = (payments || []).reduce((s, p) => {
      const y = calendarYear(p.fecha, calendarTimezone);
      if (y === currentYear) return s + (Number(p.monto) || 0);
      return s;
    }, 0);
    const totalPorCobrar = parsed
      .filter(e => getValorEvento(e) > 0 && e._date > now && Number(e.totalPagado || 0) < getValorEvento(e))
      .reduce((s, e) => s + (getValorEvento(e) - Number(e.totalPagado || 0)), 0);

    return { upcoming, thisYear: byYear[currentYear] || 0, byYear, monthly, years, totalCobrado, totalPorCobrar };
  }, [events, payments, calendarTimezone]);

  // Los siguientes 5 eventos reales (no bloqueos ni cancelados) — lo primero
  // que un dueño quiere ver al abrir el app.
  const upcomingEvents = useMemo(() => {
    const now = new Date();
    return (events || [])
      .filter((e) => e.eventType === "event" && e.status !== "cancelado")
      .map((e) => ({ ...e, _date: toJsDate(e.startTime) }))
      .filter((e) => e._date instanceof Date && !isNaN(e._date) && e._date > now)
      .sort((a, b) => a._date - b._date)
      .slice(0, 5);
  }, [events]);

  // Leads sin atender (columna Pendiente del pipeline), más recientes primero.
  // `stale` = llevan más de 3 días esperando respuesta. Los borrados del
  // pipeline (soft delete) conservan su status: se excluyen aquí, no en el
  // provider, porque Analíticas y Reportes sí deben seguir contándolos.
  const pendingLeads = useMemo(() => {
    const pending = (contactForms || []).filter(
      (f) => !f.deleted && (f.status || "pendiente") === "pendiente"
    );
    pending.sort(
      (a, b) => (toJsDate(b.createdAt)?.getTime() || 0) - (toJsDate(a.createdAt)?.getTime() || 0)
    );
    const staleCutoff = Date.now() - 3 * 24 * 60 * 60 * 1000;
    const stale = pending.filter((f) => {
      const d = toJsDate(f.createdAt);
      return d && d.getTime() < staleCutoff;
    }).length;
    return { total: pending.length, top: pending.slice(0, 5), stale };
  }, [contactForms]);

  // Eventos con saldo pendiente: los vencidos (ya pasaron y no liquidaron)
  // primero, luego los próximos por fecha. El KPI "Por cobrar" dice cuánto;
  // esta lista dice a quién perseguir.
  const cobrosPendientes = useMemo(() => {
    const now = new Date();
    const conSaldo = (events || [])
      .filter((e) => e.eventType === "event" && e.status !== "cancelado")
      .map((e) => ({
        ...e,
        _date: toJsDate(e.startTime),
        _valor: getValorEvento(e),
        _saldo: getValorEvento(e) - Number(e.totalPagado || 0),
      }))
      .filter(
        (e) =>
          e._valor > 0 &&
          e._saldo > 0 &&
          e._date instanceof Date &&
          !isNaN(e._date)
      );
    const vencidos = conSaldo.filter((e) => e._date < now).sort((a, b) => a._date - b._date);
    const proximos = conSaldo.filter((e) => e._date >= now).sort((a, b) => a._date - b._date);
    return { list: [...vencidos, ...proximos].slice(0, 6), vencidos: vencidos.length };
  }, [events]);

  // Días con al menos un evento — la métrica de ocupación que un venue
  // entiende de un vistazo. Filtrable por tipo de día (para un venue el fin
  // de semana vie–dom es el inventario que importa) y con desglose por año.
  const [ocupacionFiltro, setOcupacionFiltro] = useState("todos"); // todos | finde | semana
  const [mesDetalle, setMesDetalle] = useState(null); // {year, month} | null

  // Set de días ocupados: cada evento expande su rango start→end completo
  // (tolerante a fechas raras — ver utils/ocupacion.js).
  const occupiedSet = useMemo(() => buildOccupiedSet(events), [events]);

  const ocupacion = useMemo(() => {
    const now = new Date();

    // 12 meses hacia adelante desde el actual — los venues se reservan con
    // mucha anticipación, el dueño quiere ver el año que viene, no solo hoy.
    const months12 = [];
    for (let i = 0; i < 12; i++) {
      const m = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const info = mesOcupacion(occupiedSet, m.getFullYear(), m.getMonth(), ocupacionFiltro);
      months12.push({
        label:
          MONTHS_SHORT[m.getMonth()] +
          (m.getFullYear() !== now.getFullYear() ? ` ${String(m.getFullYear()).slice(2)}` : ""),
        year: m.getFullYear(),
        month: m.getMonth(),
        pct: info.total > 0 ? Math.round((info.ocupados / info.total) * 100) : 0,
        ocupados: info.ocupados,
        total: info.total,
        unidad: info.unidad,
      });
    }

    const years = yearsInSet(occupiedSet)
      .slice(0, 4)
      .map((year) => ({ year, ...anioOcupacion(occupiedSet, year, ocupacionFiltro) }));

    // Ocupación por día de la semana: los últimos 3 años QUE TIENEN eventos
    // (incluyendo futuros — las reservas de 2027 ya cuentan). Ignora el
    // filtro a propósito: este desglose ES el análisis por tipo de día.
    const yearsWithData = yearsInSet(occupiedSet).sort((a, b) => a - b);
    const weekYears = yearsWithData.length > 0 ? yearsWithData.slice(-3) : [now.getFullYear()];
    const porDiaSemana = ocupacionPorDiaSemana(occupiedSet, weekYears);

    return { months12, years, porDiaSemana, weekYears };
  }, [occupiedSet, ocupacionFiltro]);

  if (loading) return <Loader />;
  if (!venues.length) return null;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-1">
        <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-white">
          {greetingText}
        </h1>
      </div>
      {selectedVenue && (
        <p className="text-sm text-zinc-500 mb-6">{selectedVenue.name}</p>
      )}

      {/* Tab selector */}
      <div className="flex gap-2 mb-6">
        {[
          { key: "resumen", label: "Resumen de eventos" },
          { key: "analiticas", label: "Analíticas web" },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setHomeTab(tab.key)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
              homeTab === tab.key
                ? "text-white shadow-sm"
                : "text-zinc-500 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700"
            }`}
            style={homeTab === tab.key ? { backgroundColor: ACCENT } : undefined}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─── RESUMEN DE EVENTOS TAB ─── */}
      {homeTab === "resumen" && (
        <>
          {/* Summary cards */}
          {eventStats && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Section icon={ClockIcon} title="Eventos por venir">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-zinc-900 dark:text-white">{eventStats.upcoming}</span>
                  <InfoHint text="Eventos con fecha de inicio posterior a hoy" />
                </div>
                <div className="text-sm text-zinc-500 mt-1">próximos eventos</div>
              </Section>
              <Section icon={CalendarDaysIcon} title="Eventos este año">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-zinc-900 dark:text-white">{eventStats.thisYear}</span>
                  <InfoHint text="Total de eventos en el año actual" />
                </div>
                <div className="text-sm text-zinc-500 mt-1">en {new Date().getFullYear()}</div>
              </Section>
              <Section icon={CurrencyDollarIcon} title="Total cobrado">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-emerald-600">{formatMXN(eventStats.totalCobrado)}</span>
                  <InfoHint text="Suma de todos los pagos recibidos en el año actual" />
                </div>
                <div className="text-sm text-zinc-500 mt-1">pagos en {new Date().getFullYear()}</div>
              </Section>
              <Section icon={CurrencyDollarIcon} title="Por cobrar">
                <div className="flex items-baseline gap-2">
                  <span className={`text-2xl font-bold ${eventStats.totalPorCobrar > 0 ? "text-amber-600" : "text-zinc-900 dark:text-white"}`}>
                    {formatMXN(eventStats.totalPorCobrar)}
                  </span>
                  <InfoHint text="Saldo pendiente de todos los eventos próximos con valor definido (cotización o precio acordado)" />
                </div>
                <div className="text-sm text-zinc-500 mt-1">saldo pendiente</div>
              </Section>
            </div>
          )}

          {/* Qué sigue: próximos eventos + leads sin atender */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <Section
              icon={CalendarDaysIcon}
              title="Próximos eventos"
              className="lg:col-span-2"
              action={
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/eventos")}
                  className="inline-flex items-center gap-0.5 text-xs font-medium text-zinc-500 hover:text-[#1b4d2e]"
                >
                  Ver todos <ChevronRightIcon className="h-3.5 w-3.5" />
                </button>
              }
            >
              {upcomingEvents.length === 0 ? (
                <p className="text-sm text-zinc-500 py-4">
                  No tienes eventos próximos en el calendario.
                </p>
              ) : (
                <div className="divide-y divide-zinc-100 dark:divide-zinc-700/50 -my-1">
                  {upcomingEvents.map((e) => (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => navigate(`/dashboard/eventos/${e.id}`)}
                      className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-zinc-50 dark:hover:bg-zinc-700/30 rounded-lg px-2 -mx-2 transition"
                    >
                      <div className="w-11 shrink-0 text-center rounded-lg bg-zinc-50 dark:bg-zinc-700/40 border border-zinc-200 dark:border-zinc-700 py-1">
                        <div className="text-base font-bold leading-tight text-zinc-900 dark:text-white">
                          {e._date.getDate()}
                        </div>
                        <div className="text-[10px] uppercase tracking-wide text-zinc-500">
                          {MONTHS_SHORT[e._date.getMonth()]}
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-zinc-900 dark:text-white truncate">
                          {e.nombre || <span className="italic text-zinc-400">Sin nombre</span>}
                        </div>
                        <div className="text-xs text-zinc-400 mt-0.5">{daysAway(e._date)}</div>
                      </div>
                      <div className="shrink-0">
                        {!getValorEvento(e) ? (
                          <Badge color="zinc">Sin cotizar</Badge>
                        ) : eventoPagado(e) ? (
                          <Badge color="emerald">Pagado</Badge>
                        ) : (
                          <Badge color="yellow">Pago pendiente</Badge>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </Section>

            <Section
              icon={EnvelopeIcon}
              title="Leads por atender"
              action={
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/leads")}
                  className="inline-flex items-center gap-0.5 text-xs font-medium text-zinc-500 hover:text-[#1b4d2e]"
                >
                  Ir al pipeline <ChevronRightIcon className="h-3.5 w-3.5" />
                </button>
              }
            >
              {pendingLeads.total === 0 ? (
                <p className="text-sm text-zinc-500 py-4">
                  No tienes leads pendientes. Los nuevos formularios aparecen aquí.
                </p>
              ) : (
                <>
                  {pendingLeads.stale > 0 && (
                    <p className="mb-2 rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-800">
                      {pendingLeads.stale === 1
                        ? "1 lead lleva más de 3 días sin respuesta"
                        : `${pendingLeads.stale} leads llevan más de 3 días sin respuesta`}
                    </p>
                  )}
                  {pendingLeads.total > pendingLeads.top.length && (
                    <p className="text-xs text-zinc-400 mb-2">
                      {pendingLeads.total} pendientes en total — los más recientes:
                    </p>
                  )}
                  <div className="divide-y divide-zinc-100 dark:divide-zinc-700/50 -my-1">
                    {pendingLeads.top.map((f) => {
                      const total = leadDesgloseTotal(f);
                      return (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => navigate(`/dashboard/leads/${f.id}`)}
                          className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-zinc-50 dark:hover:bg-zinc-700/30 rounded-lg px-2 -mx-2 transition"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-zinc-900 dark:text-white truncate">
                              {f.name || "Sin nombre"}
                            </div>
                            <div className="text-xs text-zinc-400 mt-0.5">
                              {f.createdAt ? relativeTime(toJsDate(f.createdAt)) : "—"}
                            </div>
                          </div>
                          {total > 0 && (
                            <span className="shrink-0 text-xs font-semibold" style={{ color: ACCENT }}>
                              {formatMXN(total)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </Section>
          </div>

          {/* Cobranza — saldos POR EVENTO: oculta para viewers, que solo
              ven los agregados de los KPIs de arriba */}
          {!isViewer && (
          <Section
            icon={CurrencyDollarIcon}
            title="Cobros pendientes"
            className="mb-6"
            action={
              cobrosPendientes.vencidos > 0 && (
                <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                  {cobrosPendientes.vencidos} vencido{cobrosPendientes.vencidos === 1 ? "" : "s"}
                </span>
              )
            }
          >
            {cobrosPendientes.list.length === 0 ? (
              <p className="text-sm text-zinc-500 py-4">
                No tienes eventos con saldo pendiente. Todo cobrado.
              </p>
            ) : (
              <div className="divide-y divide-zinc-100 dark:divide-zinc-700/50 -my-1">
                {cobrosPendientes.list.map((e) => {
                  const vencido = e._date < new Date();
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => navigate(`/dashboard/eventos/${e.id}`)}
                      className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-zinc-50 dark:hover:bg-zinc-700/30 rounded-lg px-2 -mx-2 transition"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-zinc-900 dark:text-white truncate">
                          {e.nombre || <span className="italic text-zinc-400">Sin nombre</span>}
                        </div>
                        <div className="text-xs text-zinc-400 mt-0.5">
                          {e._date.getDate()} {MONTHS_SHORT[e._date.getMonth()]}{" "}
                          {e._date.getFullYear() !== new Date().getFullYear()
                            ? e._date.getFullYear()
                            : ""}
                        </div>
                      </div>
                      {vencido && <Badge color="amber">Vencido</Badge>}
                      <span
                        className={`shrink-0 text-sm font-semibold tabular-nums ${
                          vencido ? "text-amber-600" : "text-zinc-900 dark:text-white"
                        }`}
                      >
                        {formatMXN(e._saldo)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </Section>
          )}

          {/* Ocupación */}
          <Section
            icon={CalendarDaysIcon}
            title="Ocupación"
            className="mb-6"
            action={
              <div className="flex gap-1">
                {[
                  { key: "todos", label: "Todos los días" },
                  { key: "finde", label: "Fin de semana" },
                  { key: "semana", label: "Entre semana" },
                ].map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setOcupacionFiltro(f.key)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition ${
                      ocupacionFiltro === f.key
                        ? "text-white shadow-sm"
                        : "text-zinc-500 bg-zinc-100 hover:bg-zinc-200"
                    }`}
                    style={ocupacionFiltro === f.key ? { backgroundColor: ACCENT } : undefined}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            }
          >
            <p className="text-xs text-zinc-400 -mt-2 mb-4">
              {ocupacionFiltro === "finde"
                ? "Porcentaje de fines de semana (viernes a domingo) con al menos un evento — un fin de semana cuenta ocupado si cualquiera de sus tres días tiene evento."
                : ocupacionFiltro === "semana"
                  ? "Porcentaje de días entre semana (lunes a jueves) con al menos un evento."
                  : "Porcentaje de días con al menos un evento. Los eventos de varios días cuentan todos sus días."}{" "}
              Los próximos 12 meses reflejan lo ya reservado. Haz clic en un mes
              para ver su calendario.
            </p>

            {/* Próximos 12 meses */}
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={ocupacion.months12} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
                <CartesianGrid stroke="#f4f4f5" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={{ stroke: "#e4e4e7" }} />
                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={false} />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="rounded-lg border border-zinc-200 bg-white p-3 shadow-lg text-sm">
                        <div className="font-semibold text-zinc-900 mb-0.5">{label}</div>
                        <div className="text-zinc-600">
                          {d.ocupados} de {d.total} {d.unidad} ({d.pct}%)
                        </div>
                        <div className="text-xs text-zinc-400 mt-1">Clic para ver el calendario</div>
                      </div>
                    );
                  }}
                  cursor={{ fill: "#f4f4f5" }}
                />
                <Bar
                  dataKey="pct"
                  name="Ocupación"
                  fill={ACCENT}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={24}
                  cursor="pointer"
                  onClick={(d) => d && setMesDetalle({ year: d.year, month: d.month })}
                />
              </BarChart>
            </ResponsiveContainer>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Por día de la semana, año actual vs anterior */}
              <div className="lg:col-span-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-2">
                  Por día de la semana
                </p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={ocupacion.porDiaSemana} margin={{ top: 5, right: 12, left: -12, bottom: 0 }}>
                    <CartesianGrid stroke="#f4f4f5" vertical={false} />
                    <XAxis dataKey="dia" tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={{ stroke: "#e4e4e7" }} />
                    <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={false} />
                    <Tooltip
                      content={<CustomTooltip valueFormatter={(v) => `${v}%`} />}
                      cursor={{ fill: "#f4f4f5" }}
                    />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                    {ocupacion.weekYears.map((y, i) => {
                      const palette = [YEAR_COLORS.prev, YEAR_COLORS.current, YEAR_COLORS.next];
                      const offset = ocupacion.weekYears.length === 1 ? 1 : 0;
                      return (
                        <Bar
                          key={y}
                          dataKey={String(y)}
                          name={String(y)}
                          fill={palette[i + offset]}
                          radius={[4, 4, 0, 0]}
                          maxBarSize={20}
                        />
                      );
                    })}
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Por año */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-2">
                  Por año
                </p>
                {ocupacion.years.length === 0 ? (
                  <p className="text-sm text-zinc-500">Aún no hay eventos registrados.</p>
                ) : (
                  <div className="space-y-3">
                    {ocupacion.years.map((y) => (
                      <div key={y.year}>
                        <div className="flex items-baseline justify-between mb-1">
                          <span className="text-sm font-semibold text-zinc-900 dark:text-white">
                            {y.year}
                          </span>
                          <span className="text-xs text-zinc-500 tabular-nums">
                            {y.ocupados}/{y.total} {y.unidad} ·{" "}
                            {Math.round((y.ocupados / y.total) * 100)}%
                          </span>
                        </div>
                        <div className="h-2 w-full rounded-full overflow-hidden" style={{ backgroundColor: "#d4e3da" }}>
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.min((y.ocupados / y.total) * 100, 100)}%`,
                              backgroundColor: ACCENT,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Section>

          <OcupacionMesDialog
            open={mesDetalle !== null}
            onClose={() => setMesDetalle(null)}
            mes={mesDetalle}
            occupiedSet={occupiedSet}
            filtro={ocupacionFiltro}
          />

          {/* Charts */}
          {eventStats && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              {/* Monthly comparison chart — spans 2 cols */}
              <Section icon={ChartBarIcon} title="Eventos por mes" className="lg:col-span-2">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={eventStats.monthly} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
                    <CartesianGrid stroke="#f4f4f5" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={{ stroke: "#e4e4e7" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f4f4f5" }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                    {eventStats.years.map((y, i) => {
                      const palette = [YEAR_COLORS.prev, YEAR_COLORS.current, YEAR_COLORS.next];
                      const offset = eventStats.years.length === 1 ? 1 : 0;
                      return (
                        <Bar
                          key={y}
                          dataKey={String(y)}
                          name={String(y)}
                          fill={palette[i + offset]}
                          radius={[4, 4, 0, 0]}
                          maxBarSize={16}
                        />
                      );
                    })}
                  </BarChart>
                </ResponsiveContainer>
              </Section>

              {/* Events per year */}
              <Section icon={CalendarDaysIcon} title="Eventos por año">
                {(() => {
                  const rows = Object.entries(eventStats.byYear)
                    .map(([year, count]) => ({ year: Number(year), count }))
                    .sort((a, b) => b.year - a.year)
                    .slice(0, 6);
                  const max = Math.max(1, ...rows.map((r) => r.count));
                  return (
                    <div className="space-y-3 pt-1">
                      {rows.map((r) => (
                        <div key={r.year}>
                          <div className="flex items-baseline justify-between mb-1">
                            <span className="text-sm font-semibold text-zinc-900 dark:text-white">
                              {r.year}
                              {r.year > new Date().getFullYear() && (
                                <span className="ml-1.5 text-[10px] font-medium uppercase tracking-wide text-zinc-400">
                                  reservado
                                </span>
                              )}
                            </span>
                            <span className="text-xs text-zinc-500 tabular-nums">
                              {r.count} evento{r.count === 1 ? "" : "s"}
                            </span>
                          </div>
                          <div className="h-2 w-full rounded-full overflow-hidden" style={{ backgroundColor: "#d4e3da" }}>
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${(r.count / max) * 100}%`,
                                backgroundColor: ACCENT,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </Section>
            </div>
          )}

          {!eventStats && (
            <Section icon={CalendarDaysIcon} title="Sin datos">
              <p className="text-sm text-zinc-500">No hay eventos registrados aún.</p>
            </Section>
          )}
        </>
      )}

      {/* ─── ANALÍTICAS WEB TAB ─── */}
      {homeTab === "analiticas" && <AnaliticasWeb />}
    </div>
  );
};

export default Home;
